import {
  CELEBRATION_BURST_AT_MS,
  CELEBRATION_BURST_ORIGINS,
  CELEBRATION_BURST_STAGGER_MS,
  CELEBRATION_DURATION_MS,
  CELEBRATION_FADE_START_MS,
} from "@/shared/celebrationTiming.ts";
import confetti from "canvas-confetti";
import { useEffect, useRef } from "react";

/**
 * The celebration: a glow rising off the bottom edge with a confetti volley
 * off its crest.
 *
 * Purely visual, and deliberately so — whether it is allowed to play, and any
 * record that it did, belong to whoever mounts it. Mounting this component *is*
 * the celebration starting, which is the same contract native's overlay keeps.
 *
 * Separate from `markdown/Celebration.tsx`, which fires on a message carrying
 * `[text](action:celebrate)`: the choreography is not a markdown concern, so
 * any surface — the tasks list on clearing its last open task, as much as a
 * message — can mount it. The gating stays with each surface.
 *
 * It draws over whatever is on screen rather than behind a veil: in onboarding
 * the line being celebrated stays visible under the volley, and that is the
 * moment this is.
 */
export const CelebrationOverlay = ({
  onStarted,
  onDone,
}: {
  /**
   * The choreography is visibly underway — the glow is up and the volley is
   * firing.
   *
   * Separate from mounting, because a caller that records the celebration must
   * not record one that painted nothing — so it is reported once the glow has
   * risen and the volley is firing, not at effect start. Called at once where
   * reduced motion means nothing will paint at all, since the moment still
   * counts.
   */
  onStarted?: () => void;
  /** Nothing of the celebration is on screen any more. */
  onDone?: () => void;
}) => {
  /**
   * Held in a ref so the choreography below depends on nothing at all.
   *
   * Mounting this is the celebration, so it must run exactly once for the life
   * of the component — and a caller that passes an inline arrow would otherwise
   * hand it a new function every render and start the volley again on each one.
   * Kept honest rather than silenced: with only a stable ref read inside, an
   * empty dependency list is the exhaustive one.
   */
  const callbacks = useRef({ onStarted, onDone });
  useEffect(() => {
    callbacks.current = { onStarted, onDone };
  });

  useEffect(() => {
    // The choreography respects reduced motion as one unit: no glow, no burst —
    // and the moment still counts, so it is reported as started rather than
    // replayed if the preference ever changes.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      callbacks.current.onStarted?.();
      callbacks.current.onDone?.();
      return;
    }

    // The glow rises from below the viewport like a reverse window shade, the
    // burst fires off its crest, and shortly after it slides back down. All of
    // it is throwaway DOM outside React, so the cleanup below is what keeps a
    // mid-celebration unmount from stranding a fixed band over the next page.
    const glow = document.createElement("div");
    glow.className = "celebration-glow";
    document.body.appendChild(glow);

    // The library's default path renders in a blob-URL worker, which our CSP
    // (script-src with no worker-src) blocks; the canvas hands control to a
    // dead worker and the burst silently paints nothing. Render on the main
    // thread on a canvas of our own instead.
    const canvas = document.createElement("canvas");
    canvas.style.cssText = "position:fixed;inset:0;pointer-events:none;z-index:100";
    // Size the bitmap explicitly: the library's `resize` option never sized it
    // here, leaving a 300x150 default stretched over the viewport, which
    // renders the burst as a few frames of invisible blur. The celebration
    // lives ~3s, so tracking window resizes is not worth the option.
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    document.body.appendChild(canvas);
    const burst = confetti.create(canvas, { resize: false, useWorker: false });

    const timers: ReturnType<typeof setTimeout>[] = [];
    // Two frames rather than one: the first paints the glow at rest below the
    // viewport, so the risen state actually transitions instead of snapping.
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => glow.setAttribute("data-risen", ""));
    });

    // Three bursts spaced across the bottom edge, firing a few ms apart in a
    // freshly shuffled order every replay (1-2-3, 3-1-2, ...), so the volley
    // reads as fireworks rather than one stamp. The canvas leaves only after
    // the last one finishes.
    const slots = [...CELEBRATION_BURST_ORIGINS.keys()];
    for (let i = slots.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [slots[i], slots[j]] = [slots[j], slots[i]];
    }
    // The bursts and the glow each finish on their own clock — confetti is
    // paced in frames, so a dropped frame stretches its fall — and the
    // celebration is over only when the last of them is.
    let bursts = CELEBRATION_BURST_ORIGINS.length;
    let parts = bursts + 1;
    const partDone = () => {
      parts -= 1;
      if (parts === 0) callbacks.current.onDone?.();
    };
    const burstDone = () => {
      bursts -= 1;
      if (bursts === 0) canvas.remove();
      partDone();
    };

    timers.push(
      // The volley fires off the glow's crest, just as its rise lands.
      ...CELEBRATION_BURST_ORIGINS.map((x, i) =>
        setTimeout(
          () => {
            const animation = burst({
              particleCount: 80,
              spread: 100,
              startVelocity: 55,
              origin: { x, y: 1 },
            });
            if (animation !== null) void animation.then(burstDone);
            else burstDone();
          },
          CELEBRATION_BURST_AT_MS +
            slots[i] * CELEBRATION_BURST_STAGGER_MS +
            Math.random() * CELEBRATION_BURST_STAGGER_MS,
        ),
      ),
      // Glow risen, volley firing: the choreography is visibly underway, which
      // is the point a caller may record it.
      setTimeout(() => callbacks.current.onStarted?.(), CELEBRATION_FADE_START_MS),
      // The glow starts dissolving in place right after the crest; its fade is
      // paced to the burst, so glow and confetti end together...
      setTimeout(() => glow.setAttribute("data-fading", ""), CELEBRATION_FADE_START_MS),
      // ...and once it has played out, the node goes.
      setTimeout(() => {
        glow.remove();
        partDone();
      }, CELEBRATION_DURATION_MS),
    );

    return () => {
      cancelAnimationFrame(raf);
      for (const t of timers) clearTimeout(t);
      // Stops the confetti's own frame loop, which would otherwise keep
      // drawing on a canvas no longer in the document.
      burst.reset();
      glow.remove();
      canvas.remove();
    };
  }, []);

  return null;
};
